import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { UserData } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { useLanguage } from '../../context/LanguageContext';

const formatDate = (date: Date): string => {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
};

const AdminUsers = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersList = usersSnapshot.docs.map(d => ({
          ...d.data(),
          id: d.id,
          createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : new Date(d.data().createdAt || 0),
        })) as UserData[];
        setUsers(usersList);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error(t('admin.errorLoadingUsers'));
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [t]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      setUpdatingUser(userId);
      await updateDoc(doc(db, 'users', userId), { role: newRole });
      setUsers(prev =>
        prev.map(u => u.uid === userId ? { ...u, role: newRole as 'requester' | 'provider' | 'admin' } : u)
      );
      toast.success(t('admin.userRoleUpdated'));
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error(t('admin.errorUpdatingRole'));
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleTestStatusChange = async (userId: string, isTestUser: boolean) => {
    try {
      setUpdatingUser(userId);
      await updateDoc(doc(db, 'users', userId), { isTestUser });
      setUsers(prev =>
        prev.map(u => u.uid === userId ? { ...u, isTestUser } : u)
      );
      toast.success(`User ${isTestUser ? 'marked as TEST' : 'unmarked as TEST'} successfully`);
    } catch (error) {
      console.error('Error updating test status:', error);
      toast.error('Error updating test status');
    } finally {
      setUpdatingUser(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const byDate = (a: UserData, b: UserData) => a.createdAt.getTime() - b.createdAt.getTime();
  const requesters = users.filter(u => u.role === 'requester').sort(byDate);
  const providers = users.filter(u => u.role === 'provider').sort(byDate);

  const UserTable = ({ list }: { list: UserData[] }) => (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.user')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.role')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                TEST
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Inscription
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {list.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-6 text-center text-sm text-gray-400 italic">
                  Aucun utilisateur.
                </td>
              </tr>
            ) : (
              list.map(user => (
                <tr key={user.uid}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.username}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.role === 'admin'
                        ? 'bg-gray-200 text-gray-800'
                        : user.role === 'requester'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={user.isTestUser || false}
                        onChange={e => handleTestStatusChange(user.uid, e.target.checked)}
                        disabled={updatingUser === user.uid}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-900">{user.isTestUser ? 'TEST' : ''}</span>
                    </label>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <select
                      value={user.role}
                      onChange={e => handleRoleChange(user.uid, e.target.value)}
                      disabled={updatingUser === user.uid}
                      className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="requester">{t('admin.requesters')}</option>
                      <option value="provider">{t('admin.providers')}</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-8">{t('admin.manageUsers')}</h1>

      {/* Block 1 - Demandeurs */}
      <section className="mb-10">
        <div className="flex items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Demandeurs</h2>
          <span className="ml-2 text-sm text-gray-400">({requesters.length})</span>
        </div>
        <UserTable list={requesters} />
      </section>

      {/* Block 2 - Prestataires */}
      <section>
        <div className="flex items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Prestataires</h2>
          <span className="ml-2 text-sm text-gray-400">({providers.length})</span>
        </div>
        <UserTable list={providers} />
      </section>
    </div>
  );
};

export default AdminUsers;
